import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { TranslationKeys } from 'app/translations';
import { useSnakeBar } from 'app/hooks';
import { useBreadcrumb } from 'app/components/Breadcrumbs';
import useLocale from 'app/components/Intl/useLocale';
import {
  useUsageInvoicingOwnersQuery,
  useUsageInvoicingEmployersQuery,
  useUsageInvoicingInsurersQuery,
  useUsageInvoicingExportMutation,
} from 'app/hooks/api/admin';
import type { UsageInvoicingProduct } from '../../Services';
import { INITIAL_FILTERS, downloadCsvBlob, formatDateValue } from './types';

export const useProductInvoicingPage = () => {
  const intl = useIntl();
  const { locale } = useLocale();
  const { showErrorSnakeBar } = useSnakeBar();

  useBreadcrumb(TranslationKeys.menu_adminPanel_productInvoicing);

  const [filters, setFilters] = useState(INITIAL_FILTERS);

  const { data: owners, isLoading: isLoadingOwners } = useUsageInvoicingOwnersQuery({});
  const { data: employers, isLoading: isLoadingEmployers } = useUsageInvoicingEmployersQuery({});
  const { data: insurers, isLoading: isLoadingInsurers } = useUsageInvoicingInsurersQuery({});

  const { mutateAsync: exportInvoicing, isLoading: isExporting } = useUsageInvoicingExportMutation();

  const selectedOwner = useMemo(
    () => owners?.find(owner => owner.id === filters.ownerId) ?? null,
    [owners, filters.ownerId],
  );

  const selectedInsurer = useMemo(
    () => insurers?.find(insurer => insurer.id === filters.insurerId) ?? null,
    [insurers, filters.insurerId],
  );

  const filteredEmployers = useMemo(
    () => (filters.ownerId ? (employers ?? []).filter(employer => employer.ownerId === filters.ownerId) : []),
    [employers, filters.ownerId],
  );

  const selectedEmployer = useMemo(
    () => filteredEmployers.find(employer => employer.id === filters.employerId) ?? null,
    [filteredEmployers, filters.employerId],
  );

  const availableProducts: UsageInvoicingProduct[] = useMemo(() => selectedInsurer?.products ?? [], [selectedInsurer]);

  const selectedProduct = useMemo(
    () => availableProducts.find(product => product.productId === filters.productId) ?? null,
    [availableProducts, filters.productId],
  );

  const handleOwnerChange = useCallback((ownerId: string) => {
    setFilters(prev => ({ ...prev, ownerId, employerId: '' }));
  }, []);

  const handleEmployerChange = useCallback((employerId: string) => {
    setFilters(prev => ({ ...prev, employerId }));
  }, []);

  const handleInsurerChange = useCallback((insurerId: string) => {
    setFilters(prev => ({ ...prev, insurerId, productId: '' }));
  }, []);

  const handleProductChange = useCallback((productId: string) => {
    setFilters(prev => ({ ...prev, productId }));
  }, []);

  const handleDateFromChange = useCallback((date: Date | null) => {
    setFilters(prev => ({ ...prev, dateFrom: formatDateValue(date) }));
  }, []);

  const handleDateToChange = useCallback((date: Date | null) => {
    setFilters(prev => ({ ...prev, dateTo: formatDateValue(date) }));
  }, []);

  const handleSkipZeroResultsChange = useCallback((skipZeroResults: boolean) => {
    setFilters(prev => ({ ...prev, skipZeroResults }));
  }, []);

  const handleReset = useCallback(() => {
    setFilters(INITIAL_FILTERS);
  }, []);

  const handleExport = useCallback(async () => {
    if (!filters.ownerId) return;

    try {
      const response = await exportInvoicing({
        owner: filters.ownerId,
        employer: filters.employerId || undefined,
        insurer: filters.insurerId || undefined,
        product: filters.productId || undefined,
        dateFrom: filters.dateFrom || undefined,
        dateTo: filters.dateTo || undefined,
        skipZeroResults: filters.skipZeroResults || undefined,
      });
      downloadCsvBlob(response.data, response.headers['content-disposition']);
    } catch (err: any) {
      const message =
        err?.response?.data?.message ??
        err?.message ??
        intl.formatMessage({ id: TranslationKeys.adminPanel_productInvoicing_exportError });
      showErrorSnakeBar({ method: 'exportUsageInvoicing', message });
    }
  }, [filters, exportInvoicing, intl, showErrorSnakeBar]);

  const canExport = !!filters.ownerId && !isExporting;

  return {
    filters,
    locale,
    owners: owners ?? [],
    isLoadingOwners,
    filteredEmployers,
    isLoadingEmployers,
    insurers: insurers ?? [],
    isLoadingInsurers,
    availableProducts,
    selectedOwner,
    selectedEmployer,
    selectedInsurer,
    selectedProduct,
    isExporting,
    canExport,
    handleOwnerChange,
    handleEmployerChange,
    handleInsurerChange,
    handleProductChange,
    handleDateFromChange,
    handleDateToChange,
    handleSkipZeroResultsChange,
    handleReset,
    handleExport,
  };
};
